import { platform, release, cpus, totalmem, hostname } from 'os';
import { version } from 'process';
import { BaseMetadataCollector } from './base.js';
import { getPackageVersion, UNRESOLVED_VERSION } from '../utils/package-version.js';
import type { SystemMetadata } from './types.js';

export class SystemMetadataCollector extends BaseMetadataCollector<SystemMetadata> {
  constructor() {
    super('system');
  }

  protected async collectMetadata(): Promise<SystemMetadata> {
    return {
      os: this.getOperatingSystem(),
      cpu: this.getCpuInfo(),
      memory: this.getMemoryInfo(),
      nodeVersion: this.getNodeVersion(),
      platform: this.getPlatform(),
      hostname: this.getHostname(),
      ...this.getReporterVersion(),
      launcher: process.env.TESTDINO_CLI_CONFIG_PATH ? 'tdpw' : 'playwright',
    };
  }

  // An unreadable package.json yields the sentinel, which must not be stored as if
  // it were a published version.
  private getReporterVersion(): Pick<SystemMetadata, 'reporterVersion'> {
    const version = getPackageVersion();
    return version === UNRESOLVED_VERSION ? {} : { reporterVersion: version };
  }

  protected getEmptyMetadata(): SystemMetadata {
    return {};
  }

  private getOperatingSystem(): string {
    let platformName = 'unknown';
    let releaseVersion = 'unknown';
    try {
      platformName = platform();
    } catch {
      /* keep default */
    }
    try {
      releaseVersion = release();
    } catch {
      /* keep default */
    }
    return `${platformName} ${releaseVersion}`;
  }

  private getCpuInfo(): string {
    try {
      const cpuList = cpus();
      if (cpuList.length === 0) return 'unknown';
      return `${cpuList[0].model.trim()} (${cpuList.length} cores)`;
    } catch {
      return 'unknown';
    }
  }

  private getMemoryInfo(): string {
    try {
      return `${(totalmem() / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    } catch {
      return 'unknown';
    }
  }

  private getNodeVersion(): string {
    try {
      return version;
    } catch {
      return 'unknown';
    }
  }

  private getPlatform(): string {
    try {
      return platform();
    } catch {
      return 'unknown';
    }
  }

  private getHostname(): string {
    try {
      return hostname();
    } catch {
      return 'unknown';
    }
  }
}
